import { useState, useEffect } from 'react';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { useShallow as createShallowSelector } from 'zustand/react/shallow';
import LocalCache from '../lib.r2/utils/LocalCache';

export interface BaseStoreState {
  isInit: boolean;
  lastSession?: number | null;
  sessionStart?: number;
  isOnline?: boolean;
  isLoaded?: boolean;
  isLoggedIn?: boolean;
  orders?: any[];
  loginError?: string;
  ordersError?: string;
  selectedOrder?: any;
  isLoginWithIdAustria?: boolean;
  orderUploads?: any[];
  orderResults?: any[];
  searchOrders?: any[];
  searchOrdersError?: string;
}

type StoreEventHandler<T extends BaseStoreState> = [
  keyof T | '__change',
  ((value: any, oldValue: any) => void) | null | undefined
];

class StoreBase<T extends BaseStoreState = BaseStoreState> {
  public shallow = true;
  public cache: LocalCache | null = null;
  public config: any = null;
  public runtime =
    typeof window !== 'undefined' && (window as any).require ? 'node' : 'web';

  public useState: UseBoundStore<StoreApi<T>> = create<T>(() => ({
    isInit: false
  } as T));

  private eventHandlers: StoreEventHandler<T>[] = [];
  private subscriber: (() => void) | null = null;

  async init(config: any): Promise<void> {
    if (!config) {
      console.error('no config defined!');
      return;
    }

    this.config = config;
    console.log(
      '---init store:',
      config.name || config.appName || '',
      config.version || ''
    );

    return new Promise(resolve => {
      this.cache = new LocalCache(
        config.name || config.appName || 'app-store',
        undefined,
        async () => {
          const lastSession = await this.cache?.getItem('lastSession');
          const sessionStart = Date.now();
          await this.cache?.setItem('lastSession', sessionStart);

          this.set({ isInit: true, lastSession, sessionStart } as Partial<T>);

          resolve();
        }
      );
    });
  }

  set<K extends keyof T>(key: K, value: T[K]): void;
  set(key: Partial<T>): void;
  set<K extends keyof T>(key: K | Partial<T>, value?: T[K]): void {
    if (typeof key === 'object') {
      this.useState.setState(key);
    } else {
      this.useState.setState({ [key]: value } as Partial<T>);
    }
  }

  get(): T;
  get<K extends keyof T>(key: K): T[K];
  get<K extends keyof T>(key?: K): T | T[K] {
    const state = this.useState.getState();
    if (key !== undefined) return state[key];
    return state;
  }

  use<K extends keyof T>(key: K): T[K];
  use<K extends keyof T>(keys: K[]): T[K][];
  use<K extends keyof T>(...keys: K[]): T[K][];
  use<K extends keyof T>(key: K | K[], ...rest: K[]): T[K] | T[K][] {
    if (!Array.isArray(key) && rest.length === 0) {
      return this.useState(state => state[key]);
    }

    return this.useState(
      createShallowSelector(state => {
        const keys = Array.isArray(key) ? key : [key, ...rest];
        return keys.map(item => state[item]);
      })
    );
  }

  on<K extends keyof T>(key: K, fnc: (value: T[K], oldValue: T[K]) => void) {
    this.eventHandlers.push([key, fnc as StoreEventHandler<T>[1]]);
    if (!this.subscriber) this.initSubscriber();
  }

  onChange(fnc: (state: T, oldState: T) => void) {
    this.eventHandlers.push(['__change', fnc as StoreEventHandler<T>[1]]);
    if (!this.subscriber) this.initSubscriber();
  }

  subscribe(callback: () => void): () => void {
    return this.useState.subscribe(callback);
  }

  private initSubscriber() {
    this.subscriber = this.useState.subscribe((state, old) => {
      this.eventHandlers.forEach(([key, fnc]) => {
        if (!fnc) return;
        if (key === '__change') fnc(state, old);
        else if (state[key] !== old[key]) fnc(state[key], old[key]);
      });
    });
  }
}

// Create a custom hook function that can be used in React components
export function createUseHook<T extends BaseStoreState>(store: StoreBase<T>) {
  function useStore<K extends keyof T>(key: K): T[K];
  function useStore<K extends keyof T>(...keys: K[]): T[K][];
  function useStore<K extends keyof T>(...keys: K[]): T[K] | T[K][] {
    const [, setState] = useState<{}>({});
    
    useEffect(() => {
      const unsubscribe = store.subscribe(() => {
        setState({});
      });
      
      return unsubscribe;
    }, []);
    
    if (keys.length === 1) {
      return store.get()[keys[0]];
    }
    return keys.map(key => store.get()[key]);
  }
  
  return useStore;
}

export default StoreBase; 
